// DemoTreeCommunity.tsx
import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, GridApi, ICellRendererParams, RowSelectedEvent, SelectionChangedEvent } from 'ag-grid-community';
// Nếu bạn dùng Ant Design icon:
import { EditOutlined, DeleteOutlined, CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import { NodataTable } from '../../../components/NoDataTable';
import { Switch } from 'antd';
import { activeArray, DataTypeUnitV2 } from '../../../api/unit/unitApi';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectorPermission } from '../../../store/slices/permissionSlice';
import { checkDisabledPermission, getUserPermissions } from '../../../until';

// ===== Helpers =====
const INDENT_STEP = 16;
const indentPx = (level?: number) => Math.max(0, level ?? 0) * INDENT_STEP;

function hasChildren(n?: DataTypeUnitV2) {
    return !!(n && n.children && n.children.length > 0);
}

/** Flatten theo trạng thái expanded; giữ nguyên thứ tự phần tử như trong mảng gốc */
function flattenTree(tree: DataTypeUnitV2[], expanded: Set<number>): DataTypeUnitV2[] {
    const out: DataTypeUnitV2[] = [];
    const dfs = (nodes: DataTypeUnitV2[]) => {
        for (const n of nodes) {
            out.push(n);
            if (hasChildren(n) && expanded.has(n.id)) {
                dfs(n.children);
            }
        }
    };
    dfs(tree);
    return out;
}

// ===== Cell Renderers =====
const ToggleCell: React.FC<
    ICellRendererParams & {
        onToggle: (id: number) => void;
        expanded: boolean;
        hasChildren: boolean;
    }
> = (p) => {
    const { data } = p as any;
    const level = (data?.level ?? 0);
    const { expanded, hasChildren, onToggle } = p as any;

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (hasChildren) onToggle(data.id);
    };



    return (
        <div >
            {/* indent */}
            <span style={{ width: indentPx(level), paddingLeft: 16 * (level - 3) }} />
            {/* caret */}
            {hasChildren ? (
                <button
                    onClick={handleToggle}
                    style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        width: 22,
                        textAlign: 'center',
                        padding: 0,
                        lineHeight: 1
                    }}
                    aria-label={expanded ? 'Thu gọn' : 'Mở rộng'}
                    title={expanded ? 'Thu gọn' : 'Mở rộng'}
                >
                    {expanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
                </button>
            ) : (
                <span style={{ display: 'inline-block' }} />
            )}
            {/* title */}
            <span style={{ paddingLeft: 8 }}>{data?.title}</span>
        </div>
    );
};

// ===== Main Grid =====
export default function TableCustomTypeTree({
    selectedIdUnit,
    dataTable,
    defaultExpandLevel = 1,
    setSelectedIdUnit,
    updateActiveUnit,
    setId,
    setOpenDeleteUnit,
    setOpenEditUnit,
    visibleColumns
}: {
    selectedIdUnit: Set<Number>;
    dataTable: DataTypeUnitV2[];
    defaultExpandLevel?: number;

    setSelectedIdUnit: (ids: Set<Number>) => void;
    updateActiveUnit: (value: number) => void
    setId: (value: number) => void
    setOpenDeleteUnit: (e: boolean) => void
    setOpenEditUnit: (e: boolean) => void
    visibleColumns?: string[]
}) {


  const location = useLocation();
  const permissionSlice = useSelector(selectorPermission)
  const { add, edit, delete: isdelete, } = getUserPermissions(permissionSlice, location.pathname)
    //#region Phần chọn nhóm 
    const isRestoringSelection = useRef(false);
    const gridApiRef = useRef<GridApi | null>(null);

    // --- thêm dưới phần helpers/ useMemo ---
    const idToDescendants = useMemo(() => {
        const map = new Map<number, number[]>();

        const collectDesc = (n: DataTypeUnitV2): number[] => {
            if (!hasChildren(n)) {
                map.set(n.id, []);
                return [];
            }
            const acc: number[] = [];
            for (const c of n.children) {
                acc.push(c.id);
                const sub = collectDesc(c);
                if (sub.length) acc.push(...sub);
            }
            map.set(n.id, acc);
            return acc;
        };

        const walk = (nodes: DataTypeUnitV2[]) => {
            for (const n of nodes) {
                collectDesc(n);
                if (hasChildren(n)) walk(n.children);
            }
        };
        walk(dataTable);
        return map;
    }, [dataTable]);

    // --- thêm trong component ---
    const onRowSelected = useCallback((event: RowSelectedEvent) => {
        const api = event.api;
        const node = event.node;
        if (!node || !node.data || isRestoringSelection.current) return;

        const id = Number(node.data.id);
        const descendants = idToDescendants.get(id) ?? [];
        if (descendants.length === 0) return; // không có con => thôi

        // Tránh vòng lặp sự kiện
        isRestoringSelection.current = true;

        // Set selected cho toàn bộ hậu duệ theo trạng thái của parent
        const shouldSelect = node.isSelected();
        const descendantSet = new Set(descendants);

        api.forEachNode((n: any) => {
            if (!n.data) return;
            const nid = Number(n.data.id);
            if (descendantSet.has(nid)) {
                n.setSelected(shouldSelect, false, true);
            }
        });

        // Cập nhật state selectedIdUnit
        const next = new Set(selectedIdUnit);
        if (shouldSelect) {
            next.add(id);
            descendants.forEach(d => next.add(d));
        } else {
            next.delete(id);
            descendants.forEach(d => next.delete(d));
        }
        setSelectedIdUnit(next);

        // Nhả cờ
        // dùng setTimeout 0 để đợi ag-grid bắn xong các event nội bộ
        setTimeout(() => { isRestoringSelection.current = false; }, 0);
    }, [idToDescendants, selectedIdUnit, setSelectedIdUnit]);



    // Khởi tạo expandedIds: mở sẵn các node có level < defaultExpandLevel và có children
    const [expandedIds, setExpandedIds] = useState<Set<number>>(() => {
        const s = new Set<number>();
        const scan = (nodes: DataTypeUnitV2[]) => {
            for (const n of nodes) {
                if (hasChildren(n) && (n.level ?? 0) < defaultExpandLevel) s.add(n.id);
                if (hasChildren(n)) scan(n.children);
            }
        };
        scan(dataTable);
        return s;
    });

    // rowData hiển thị
    const rowData = useMemo(() => flattenTree(dataTable, expandedIds), [dataTable, expandedIds]);

    //------------------------------------ Start SELECT
    // Định danh duy nhất cho mỗi row
    const getRowId = useCallback((p: { data: any }) => String(p.data.id), []);

    // --- Grid ready ---
    const onGridReady = useCallback((params: { api: GridApi }) => {
        gridApiRef.current = params.api;
    }, []);

    // --- Selection change ---
    const onSelectionChanged = useCallback((event: SelectionChangedEvent) => {
        // if (isRestoringSelection.current) return;

        // const api = event.api;
        // const selectedNodes = api.getSelectedNodes().filter(n => n.data);
        // const selectedIdsOnPage = selectedNodes.map(n => Number(n.data.id));

        // const currentPageIds = (rowData ?? []).map((row: any) => Number(row.id));

        // // --- Tạo bản sao của selectedIds hiện tại ---
        // const nextSet = new Set(selectedIdUnit);

        // // --- Xác định phần tử được thêm và bị xóa trên trang HIỆN TẠI ---
        // const added: number[] = [];
        // const removed: number[] = [];

        // // Kiểm tra từng ID trên trang hiện tại
        // for (const id of currentPageIds) {
        //     const isCurrentlySelected = selectedIdsOnPage.includes(id);
        //     const wasPreviouslySelected = selectedIdUnit.has(id);

        //     if (isCurrentlySelected && !wasPreviouslySelected) {
        //         // ID mới được chọn
        //         added.push(id);
        //         nextSet.add(id);
        //     } else if (!isCurrentlySelected && wasPreviouslySelected) {
        //         // ID bị bỏ chọn
        //         removed.push(id);
        //         nextSet.delete(id);
        //     }
        // }

        // setSelectedIdUnit(nextSet);



        if (isRestoringSelection.current) return;

        const api = event.api;
        const selectedNodes = api.getSelectedNodes().filter(n => n.data);
        const selectedIdsOnPage = selectedNodes.map(n => Number(n.data.id));

        const currentPageIds = (rowData ?? []).map((row: any) => Number(row.id));

        const nextSet = new Set(selectedIdUnit);

        for (const id of currentPageIds) {
            const selectedNow = selectedIdsOnPage.includes(id);
            const was = selectedIdUnit.has(id);
            if (selectedNow && !was) nextSet.add(id);
            else if (!selectedNow && was) nextSet.delete(id);
        }

        setSelectedIdUnit(nextSet);

        // 👉 Cập nhật danh sách selected account
    }, [selectedIdUnit, rowData]);



    // --- Khôi phục selection khi data mới load ---
    useEffect(() => {
        if (!gridApiRef.current || !rowData?.length) return;

        isRestoringSelection.current = true;

        // Xây 1 set bao gồm cả các hậu duệ của những id đang được chọn
        const expandedSelection = selectedIdUnit;
        for (const id of selectedIdUnit) {
            const desc = idToDescendants.get(Number(id)) ?? [];
            desc.forEach(d => expandedSelection.add(d));
        }

        gridApiRef.current.forEachNode((node: any) => {
            if (!node.data) return;
            const id = Number(node.data.id);
            node.setSelected(expandedSelection.has(id), false, true);
        });

        setTimeout(() => { isRestoringSelection.current = false; }, 0);
    }, [rowData, selectedIdUnit, idToDescendants]);

    //------------------------------------ End SELECT

    const onToggle = useCallback((id: number) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    // rebind khi rowData đổi

    //#region Cấu hình cột nhóm
    const columnDefs = useMemo<ColDef[]>(() => [

        {
            headerName: 'Hành động',
            field: 'action',
            colId: 'action',
            width: 180,
            resizable: false,
            suppressMovable: true,
            lockPosition: 'left',
            headerComponentParams: {
                template: '<span class="w-full flex items-center justify-center">Hành động</span>'
            },
            cellRenderer: (params: ICellRendererParams) => {
                const { id } = params.data;
                return (
                    <div className="h-full d-flex align-center justify-center gap-10-px">
                        <EditOutlined
                         className={`${checkDisabledPermission(edit)}  text-sm border-D9D9D9 p-5-px bg-white rounded-2 cursor-pointer `}
                            onClick={() => {
                                setOpenEditUnit(true);
                                setId(Number(id));
                            }}
                        />
                        <DeleteOutlined
                            className={`${checkDisabledPermission(isdelete)} text-F5222D text-sm border-EAECF0 p-5-px bg-white rounded-2 cursor-pointer`}
                            onClick={() => {
                                setOpenDeleteUnit(true);
                                setId(Number(id));
                            }}
                        />
                    </div>
                );
            }
        },

        {
            headerName: 'Mã đơn vị',
            field: 'code',
            colId: 'code',
            flex: 1,
            sortable: true,
            comparator: (valueA: string, valueB: string) => {
                return valueA.localeCompare(valueB);
            }
        },
        {

            headerName: 'Mã pháp nhân B7',
            field: 'b7EntityCode',
            colId: 'b7EntityCode',
            flex: 1,
        },
        {

            headerName: 'Đơn vị',
            field: 'title',
            colId: 'title',
            flex: 1,
            minWidth: 280,
            sortable: false, // để giữ thứ tự cây; muốn sort sibling thì viết comparator tùy biến
            cellRenderer: (p: ICellRendererParams) => {
                const node = p.data as DataTypeUnitV2;
                const _hasChildren = hasChildren(node);
                const expanded = expandedIds.has(node.id);
                return (
                    <ToggleCell
                        {...p}
                        hasChildren={_hasChildren}
                        expanded={expanded}
                        onToggle={onToggle}
                    />
                );
            },
        },
        {
            headerName: 'Toàn quốc',
            field: 'nationwideTitle',
            colId: 'nationwideTitle',
            flex: 1,
            sortable: true,
            comparator: (valueA: string, valueB: string) => {
                // Nếu cần sử dụng findParentIdLevelOne, bạn có thể dùng valueGetter
                return valueA.localeCompare(valueB);
            }
        },
        {
            headerName: 'Vùng miền',
            field: 'regionTitle',
            colId: 'regionTitle',
            flex: 1,
            sortable: true,
            comparator: (valueA: string, valueB: string) => {
                return valueA.localeCompare(valueB);
            }
        },
        {
            headerName: 'Trạng thái',
            field: 'status',
            colId: 'status',
            flex: 1,
            cellRenderer: (params: ICellRendererParams) => {
                const { id, view } = params.data;
                return (
                    <div className="d-flex align-center gap-10-px">
                        <Switch
                            defaultChecked={!!view}
                            onChange={() => updateActiveUnit(Number(id))}
                            className="rs-switch rs-switch--sm"
                        />
                        <p className="font-medium fw-500 text-344054">
                            {activeArray[view ? 1 : 0]}
                        </p>
                    </div>
                );
            }
        }
    ], [dataTable, expandedIds, onToggle]);

    const columnDefsWithVisibility = useMemo(() => {
        if (!visibleColumns) return columnDefs;
        return columnDefs.map(col => ({
            ...col,
            hide: !visibleColumns.includes(col.colId as string),
        }));
    }, [columnDefs, visibleColumns]);

    //#endregion

    useEffect(() => {
        gridApiRef.current?.setGridOption('rowData', rowData);
    }, [rowData]);
    //#endregion

    return (
        <>

            <div className={`order-history-card__body `}>
                <div className="ag-theme-alpine  custom-table-ag" style={{ width: '100%', height: 610 }}>
                    <AgGridReact
                        className="my-auto-height-grid"
                        getRowStyle={(params: any) => ({
                            backgroundColor: params?.node?.rowIndex % 2 ? '#f7f9fc' : '#ffffff',
                        })}
                        rowHeight={55}
                        rowData={rowData}
                        columnDefs={columnDefsWithVisibility}
                        alwaysShowHorizontalScroll
                        alwaysShowVerticalScroll
                        rowSelection={{ mode: 'multiRow' }}
                        overlayNoRowsTemplate={NodataTable}
                        getRowId={getRowId}
                        onGridReady={onGridReady}
                        onSelectionChanged={onSelectionChanged}
                        onRowSelected={onRowSelected}
                    />
                </div>
            </div>
        </>
    );
}
